// PATH: src/layout/AppLayout.tsx — coquille de l'application (menu latéral + contenu)
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Drawer, Grid, Layout, Menu, Segmented, Space, Tooltip, Typography } from "antd";
import {
  AppstoreOutlined, TeamOutlined, SettingOutlined, BookOutlined, DollarOutlined,
  IdcardOutlined, BankOutlined, LineChartOutlined, LogoutOutlined, ExperimentOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, ShopOutlined, NumberOutlined, SearchOutlined,
  SunOutlined, MoonOutlined, DesktopOutlined,
} from "@ant-design/icons";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import GlobalSearch from "../components/GlobalSearch";
import { useOnlineStatus } from "../hooks/useOnlineStatus";
import type { ThemeMode } from "../hooks/useThemeMode";
import { COLORS } from "../theme";

const { Sider, Content, Header } = Layout;

// Le pli du menu est une préférence de poste de travail : un agent qui replie
// le menu pour gagner en largeur ne veut pas le refaire à chaque écran.
const COLLAPSE_KEY = "tilmischool.sider.collapsed";

// Travail quotidien d'abord, réglages en bas : « Admin pédagogique » et
// « Référentiels » sont les deux écrans de configuration et se suivent.
const NAV_ITEMS = [
  { key: "dashboard", icon: <AppstoreOutlined />, label: "Tableau de bord" },
  { key: "students", icon: <TeamOutlined />, label: "Élèves" },
  { key: "pedagogie", icon: <BookOutlined />, label: "Pédagogie" },
  { key: "paiements", icon: <DollarOutlined />, label: "Paiements" },
  { key: "personnel", icon: <IdcardOutlined />, label: "Personnel" },
  { key: "administration", icon: <BankOutlined />, label: "Administration" },
  { key: "references", icon: <NumberOutlined />, label: "Références" },
  { key: "engagement", icon: <LineChartOutlined />, label: "Engagement" },
  { key: "ecole", icon: <ShopOutlined />, label: "École" },
  { key: "admin-pedagogique", icon: <ExperimentOutlined />, label: "Admin pédagogique" },
  { key: "referentiels", icon: <SettingOutlined />, label: "Référentiels" },
];

/** Bloc de marque du bandeau — partagé par le menu fixe et le tiroir mobile. */
const Brand: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div
    style={{
      height: 60,
      display: "flex",
      alignItems: "center",
      justifyContent: compact ? "center" : "flex-start",
      gap: 10,
      padding: compact ? 0 : "0 20px",
      borderBottom: "1px solid rgba(255,255,255,0.08)",
      marginBottom: 8,
    }}
  >
    <BankOutlined style={{ color: COLORS.primary, fontSize: 20, flexShrink: 0 }} />
    {/* Le nom disparaît replié : tronqué à 72px il ne dirait rien. */}
    {!compact && (
      <Typography.Text style={{ color: "#fff", fontSize: 16, fontWeight: 600, letterSpacing: "-0.01em" }}>
        TilmiSchool
      </Typography.Text>
    )}
  </div>
);

const AppLayout: React.FC<{
  themeMode: ThemeMode;
  onThemeModeChange: (mode: ThemeMode) => void;
}> = ({ themeMode, onThemeModeChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Déconnexion via le contexte : elle repasse `status` à « anonymous ».
  // Passer par useAuthApi supprimerait le cookie sans que l'application le
  // sache, laissant un état « connecté » qui ne correspond plus à rien.
  const auth = useAuth();
  const screens = Grid.useBreakpoint();

  // Sous « lg », le bandeau fixe est remplacé par un tiroir : même replié à
  // 72px il mangeait un cinquième d'un écran de téléphone en permanence, pour
  // des icônes sans libellé. `=== false` et non `!screens.lg` : la valeur est
  // indéfinie avant la première mesure, et traiter cela comme « mobile »
  // ferait clignoter le bandeau sur un poste de bureau.
  const mobile = screens.lg === false;

  // Lecture paresseuse : l'état initial vient du stockage, sinon le menu
  // s'afficherait déplié puis se replierait après le premier rendu.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false; // stockage indisponible (navigation privée) : déplié.
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* préférence non mémorisée, sans conséquence sur la session */
    }
  }, [collapsed]);

  // Le tiroir se referme au changement de route : sur un téléphone il couvre
  // tout l'écran, et le laisser ouvert masquerait la page qu'on vient d'ouvrir.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  // Recherche globale : Ctrl+K (⌘K sur Mac), raccourci attendu d'une palette.
  // preventDefault empêche le navigateur d'ouvrir SA propre barre de recherche.
  const online = useOnlineStatus();
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Entrée active déduite de NAV_ITEMS : chaque clé correspond à sa route
  // (/<clé>). Deux exceptions portées à la main : « dashboard » est la racine
  // (/), et « students » couvre /eleves comme /students/:id.
  //
  // Préfixe le plus LONG d'abord : « /admin-pedagogique » et « /administration »
  // partagent leur début, et un tri arbitraire ferait surligner la mauvaise
  // entrée.
  const selected = useMemo(() => {
    if (location.pathname === "/") return "dashboard";
    if (location.pathname.startsWith("/eleves") || location.pathname.startsWith("/students"))
      return "students";
    const match = NAV_ITEMS.map((i) => i.key)
      .filter((k) => k !== "students" && k !== "dashboard")
      .sort((a, b) => b.length - a.length)
      .find((k) => location.pathname.startsWith(`/${k}`));
    return match ?? "dashboard";
  }, [location.pathname]);

  const go = (key: string) =>
    navigate(key === "dashboard" ? "/" : key === "students" ? "/eleves" : `/${key}`);

  const nav = (
    <Menu
      theme="dark"
      mode="inline"
      inlineCollapsed={!mobile && collapsed}
      selectedKeys={[selected]}
      items={NAV_ITEMS}
      onClick={({ key }) => go(key)}
    />
  );

  const themePicker = (
    // Trois états plutôt qu'une bascule : « Système » suit le réglage du poste,
    // un choix explicite le remplace.
    <Segmented
      size="small"
      value={themeMode}
      onChange={(v) => onThemeModeChange(v as ThemeMode)}
      options={[
        { value: "light", icon: <SunOutlined />, title: "Clair" },
        { value: "system", icon: <DesktopOutlined />, title: "Système" },
        { value: "dark", icon: <MoonOutlined />, title: "Sombre" },
      ]}
    />
  );

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Replié : 72px d'icônes plutôt que 0. L'ancien collapsedWidth={0}
          escamotait toute la navigation sous « lg » sans laisser de prise pour
          la rouvrir depuis le menu lui-même. */}
      {!mobile && (
        <Sider
          collapsedWidth={72}
          width={232}
          collapsed={collapsed}
          trigger={null}
          style={{ position: "sticky", top: 0, height: "100vh", overflow: "auto" }}
        >
          <Brand compact={collapsed} />
          {nav}
        </Sider>
      )}

      {/* Tiroir mobile : la navigation complète, libellés compris, sur toute la
          hauteur — un menu de onze entrées ne se parcourt pas en icônes. */}
      <Drawer
        placement="left"
        open={mobile && drawerOpen}
        onClose={() => setDrawerOpen(false)}
        closable={false}
        width={264}
        // Le fond (même dégradé marine que le bandeau) est posé par la classe,
        // pas ici : un style en ligne l'emporterait sur la règle CSS.
        styles={{ body: { padding: 0 } }}
        className="app-nav-drawer"
      >
        <Brand />
        {nav}
        {/* Apparence et déconnexion vivent ici sur mobile : l'en-tête n'a la
            place que pour le menu, la marque et la recherche. */}
        <div style={{ padding: 16, marginTop: 8, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {themePicker}
            <Button
              block
              type="text"
              icon={<LogoutOutlined />}
              style={{ color: "#B9C4D4", textAlign: "left" }}
              onClick={() => auth.logout().finally(() => navigate("/login", { replace: true }))}
            >
              Déconnexion
            </Button>
          </Space>
        </div>
      </Drawer>

      <Layout>
        {/* En-tête collant : la déconnexion reste atteignable en bas d'une
            longue liste, sans remonter. */}
        <Header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
            borderBottom: "1px solid var(--app-border)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          {/* La bascule vit dans l'en-tête, pas dans le menu : elle reste au
              même endroit que le menu soit ouvert ou replié. */}
          <Space size={8}>
            <Tooltip
              title={mobile ? "Menu" : collapsed ? "Déplier le menu" : "Replier le menu"}
              placement="bottomLeft"
            >
              <Button
                type="text"
                aria-label={mobile ? "Ouvrir le menu" : collapsed ? "Déplier le menu" : "Replier le menu"}
                aria-expanded={mobile ? drawerOpen : !collapsed}
                icon={mobile || collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => (mobile ? setDrawerOpen(true) : setCollapsed((v) => !v))}
              />
            </Tooltip>
            {/* Sur mobile le bandeau latéral est caché : sans ce rappel, rien
                à l'écran ne dit dans quelle application on se trouve. */}
            {mobile && (
              <Typography.Text style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>
                TilmiSchool
              </Typography.Text>
            )}
            {/* Le raccourci est affiché : une palette qu'on ne découvre qu'en
                devinant Ctrl+K n'est utilisée que par ceux qui la connaissent.
                Il disparaît sur mobile, où il n'y a pas de clavier physique. */}
            {!mobile && (
              <Button
                icon={<SearchOutlined />}
                aria-label="Recherche globale"
                onClick={() => setSearchOpen(true)}
              >
                Rechercher
                <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                  Ctrl K
                </Typography.Text>
              </Button>
            )}
          </Space>
          <Space size={8}>
            {mobile ? (
              <Button
                type="text"
                icon={<SearchOutlined />}
                aria-label="Recherche globale"
                onClick={() => setSearchOpen(true)}
              />
            ) : (
              <>
                <Tooltip title="Apparence">{themePicker}</Tooltip>
                <Button
                  type="text"
                  icon={<LogoutOutlined />}
                  onClick={() => auth.logout().finally(() => navigate("/login", { replace: true }))}
                >
                  Déconnexion
                </Button>
              </>
            )}
          </Space>
        </Header>
        {/* Bandeau hors ligne : persistant tant que la connexion manque. Une
            saisie faite dans cet état échouera — mieux vaut le dire avant
            qu'après. */}
        {!online && (
          <Alert
            banner
            type="warning"
            showIcon
            message="Hors ligne — les enregistrements échoueront tant que la connexion n'est pas rétablie."
          />
        )}
        <Content className="app-content">
          {/* Clé = route : le fondu montant se rejoue à chaque navigation,
              donnant une continuité spatiale entre les écrans (index.css). */}
          <div key={location.pathname} className="app-fade-in">
            <Outlet />
          </div>
        </Content>
        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      </Layout>
    </Layout>
  );
};

export default AppLayout;
